import React, { useState, useEffect } from 'react';
import { Copy, Check, Users, DollarSign, TrendingUp, Gift } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function Referrals() {
  const { user } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, [user?.tenant_id]);

  const fetchReferralData = async () => {
    if (!user?.tenant_id) return;

    try {
      setLoading(true);
      
      // Call generate-referral-code to get/create code
      const codeResponse = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-referral-code`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ tenant_id: user.tenant_id })
        }
      );
      
      const codeData = await codeResponse.json();
      
      // Call get-referral-stats
      const statsResponse = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-referral-stats`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ tenant_id: user.tenant_id })
        }
      );
      
      const statsData = await statsResponse.json();
      
      setReferralData({
        code: codeData.code,
        url: statsData.referral_url,  // ← Get URL from stats response instead
        ...statsData.stats
      });
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-indigo-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Loading referral dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Referral Program</h1>
        <p className="text-gray-600 mt-1">
          Earn credits by referring new customers to SurFox Engage
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Referrals</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {referralData?.active_credited || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Additional Active</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {referralData?.active_uncredited || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Not earning credits yet</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Trials</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {referralData?.pending || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Gift className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Waiting for conversion</p>
        </div>

        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-indigo-100">Monthly Credits</p>
              <p className="text-2xl font-bold mt-1">
                ${referralData?.total_monthly_credits || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          </div>
          <p className="text-xs text-indigo-100 mt-2">Applied to your next bill</p>
        </div>
      </div>

      {/* Referral Link Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Referral Link</h2>
        
        <div className="flex items-center space-x-3">
          <input
            type="text"
            value={referralData?.url || ''}
            readOnly
            className="flex-1 px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono"
          />
          <button
            onClick={() => copyToClipboard(referralData?.url)}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
          >
            {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
        </div>

        <p className="text-sm text-gray-600 mt-4">
          Share this link with other businesses. When they sign up and become paying customers, you'll earn monthly credits!
        </p>
      </div>

      {/* How It Works */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">How It Works</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-3">
              <span className="text-indigo-600 font-bold">1</span>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Share Your Link</h3>
            <p className="text-sm text-gray-600">
              Send your unique referral link to other businesses who could benefit from SurFox Engage.
            </p>
          </div>

          <div>
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-3">
              <span className="text-indigo-600 font-bold">2</span>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">They Subscribe</h3>
            <p className="text-sm text-gray-600">
              When they sign up and convert to a paying customer, they get 20% off their first month.
            </p>
          </div>

          <div>
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-3">
              <span className="text-indigo-600 font-bold">3</span>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">You Earn Credits</h3>
            <p className="text-sm text-gray-600">
              Get $25-$200/month in credits per referral (based on their plan), applied to your bill up to your own monthly subscription amount.
            </p>
          </div>
        </div>
      </div>

      {/* Your Active Referrals */}
      {(referralData?.credited_referrals?.length > 0 || referralData?.uncredited_referrals?.length > 0) && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Your Active Referrals</h2>
          <p className="text-sm text-gray-600 mb-4">
            Earning ${referralData?.total_monthly_credits || 0} of a possible ${referralData?.referrer_plan_price || 0}/month cap (your {referralData?.referrer_plan || 'plan'} plan price).
          </p>

          <div className="space-y-2">
            {referralData?.credited_referrals?.map((ref, i) => (
              <div key={ref.id || `c-${i}`} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900 capitalize">{ref.referee_plan} plan referral</p>
                  {ref.partially_capped && (
                    <p className="text-xs text-gray-600 mt-0.5">
                      Capped at remaining budget (full credit ${ref.full_credit})
                    </p>
                  )}
                </div>
                <span className="text-lg font-bold text-green-600">+${ref.applied_credit}/mo</span>
              </div>
            ))}

            {referralData?.uncredited_referrals?.map((ref, i) => (
              <div key={ref.id || `u-${i}`} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div>
                  <p className="font-medium text-gray-700 capitalize">{ref.referee_plan} plan referral</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Tracked, but your monthly cap is full (full credit would be ${ref.full_credit})
                  </p>
                </div>
                <span className="text-sm font-medium text-gray-500">$0/mo</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Credit Structure */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Credit Structure</h2>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Starter Plan Referral</p>
              <p className="text-sm text-gray-600">$147/month subscription</p>
            </div>
            <span className="text-lg font-bold text-green-600">$25/month</span>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Growth Plan Referral</p>
              <p className="text-sm text-gray-600">$597/month subscription</p>
            </div>
            <span className="text-lg font-bold text-green-600">$100/month</span>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Scale Plan Referral</p>
              <p className="text-sm text-gray-600">$2,497/month subscription</p>
            </div>
            <span className="text-lg font-bold text-green-600">$200/month</span>
          </div>
        </div>

        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Your total monthly credits are capped at your own subscription price, so your bill can be reduced to $0 but never below.
            Highest-value referrals are credited first. Any referrals beyond your cap stay tracked and start earning credits if a credited referral churns.
          </p>
        </div>
      </div>
    </div>
  );
}